import { Platform } from "react-native";
import { IFullArticleDetails } from "../features/immersive-news-article/types";
import i18n from "../localization/i18n";
import { ArticleResponse, Category, PageParam, RecommendedCollection } from "../shared/types/DataTypes";
import apiClient from "./apiClient";
import { getFirebaseToken } from "./authService/authService";
import { debounce } from "../shared/helpers/utils";

const API_URLS = {
    CONFIG: '/api/config/{key}',
    CATEGORIES: '/api/categories?{query}',
    ARTICLES: '/api/articles?{query}',
    ARTICLE_DETAILS: '/api/articles/{id}?{query}',
    ARTICLE_SOURCES: '/api/articles/{id}/sources/?{query}',
    TRANSCRIBE: 'api/voice/transcribe',
    REELS: 'api/ts/reels',
    SHORTS: 'api/ts/shorts',
    PROFILE: 'api/firebase/auth/profile',
    UPDATE_PROFILE: 'api/user/profile',
    REGISTER_DEVICE: 'api/user/register-device',
    MIGRATE_ANONYMOUS_USER: 'api/user/migrate-user',
    TAGS: 'api/tags?{query}',
    CONTEXTUAL_TAGS: 'api/tags/contextual',
    SAVE_COLLECTION: 'api/collections/save',
    COLLECTIONS: 'api/collections',
    THINKO_COLLECTIONS: 'api/collections/thinko',
    COLLECTION_PREVIEW: '/api/collections/preview?{query}',
    GET_RECOMMENDATIONS: 'api/collections/recommended?{query}',
    GET_COLLECTION_By_ID: 'api/collections/{collectionId}',
    DELETE_COLLECTIONS: 'api/collections/delete/{collectionId}',
    AI_QUERY: '/api/assistant/query?{query}',
    AI_ARTICLE_FAQS: '/api/assistant/events/{eventId}/faqs?{query}',
    AI_ARTICLE_QUERY: '/api/assistant/events/{eventId}/query?{query}',
    AI_TRENDING_REFRESH: '/api/assistant/trending/refresh'
}

enum APP_CONFIG_KEY  {
    ONBOARDING_INTERESTS = 'onboarding_interests',
    ASSISTANT_SUGGESTIONS = 'assistant_suggestions',  
}
const getConfig = async (key:APP_CONFIG_KEY): Promise<any> => {
    const url = buildPath(API_URLS.CONFIG, { key});
    return await getData(url);
};
const getUser = async (): Promise<any> => {
    const params = localeSearchParams();
    const url = buildPath(API_URLS.PROFILE, { query: params.toString() });
    return await getData(url);
}
const updateUser = async (interests: any[]): Promise<any> => {
    return await postData(API_URLS.UPDATE_PROFILE, { interests });
}

const registerDevice = async (deviceId: string, fcmToken: string): Promise<any> => {
    return await postData(API_URLS.REGISTER_DEVICE, { device_id: deviceId, fcm_token: fcmToken, platform: Platform.OS });
};

const migrateAnonymousUser = async (anonUid: string) => {
    await await postData(API_URLS.MIGRATE_ANONYMOUS_USER, { anonymousUid: anonUid });
}

const fetchCategories = async (): Promise<Category[]> => {
    const params = localeSearchParams();
    const url = buildPath(API_URLS.CATEGORIES, { query: params.toString() });
    return await getData(url);
}

const fetchSources = async (articleId: string): Promise<any[]> => {
    const params = localeSearchParams();
    const url = buildPath(API_URLS.ARTICLE_SOURCES, { id: articleId, query: params.toString() });
    return await getData(url);
}

const fetchArticles = async (pageParams: PageParam): Promise<ArticleResponse> => {
    const params = localeSearchParams();
    params.append("limit", "20");
    let apiResponse: ArticleResponse;

    if (pageParams?.cursor) {
        const paramName =
            pageParams.direction === 'older' ? 'after' : 'before';
        params.append(paramName, pageParams.cursor);
    }

    if (pageParams?.collectionId) {
        params.append("collectionId", pageParams.collectionId);
    }

    const url = buildPath(API_URLS.ARTICLES, { query: params.toString() });
    apiResponse = await getData(url);

    return apiResponse
};


const previewArticles = async (orTags: string[], andTag?: string): Promise<ArticleResponse> => {
    const params = localeSearchParams();
    const url = buildPath(API_URLS.COLLECTION_PREVIEW, { query: params.toString() });
    return await postData(url, { orTags, andTag });
};

const fetchFullArticleDetails = async (articleId: string): Promise<IFullArticleDetails> => {
    const params = localeSearchParams();
    const url = buildPath(API_URLS.ARTICLE_DETAILS, { id: articleId, query: params.toString() });
    const apiResponse = await getData(url)
    // Transform API response to match IFullArticleDetails interface
    return {
        id: apiResponse.id,
        image: apiResponse.image || '',
        // category: apiResponse.category,
        date: apiResponse.date || new Date().toISOString(),
        sourceCount: apiResponse.sourceCount || 0,
        favicons: apiResponse.favicons || [],
        title: apiResponse.title || '',
        description: apiResponse.description || '',
        impactTags: apiResponse.impactTags || [],
        contentType: apiResponse.contentType || 'text',
        gistContent: apiResponse.gistContent || [],
        storySoFar: apiResponse.storySoFar || [],
        whatsNext: apiResponse.whatsNext || [],
        imageSourceUrl: apiResponse.imageSourceUrl || '',
        audio: apiResponse.audio || '',
        audioFullStory: apiResponse.audioFullStory || '',
    };
}

const fetchReels = async (): Promise<any[]> => {
    const params = localeSearchParams();
    return await getData(API_URLS.REELS);
}

const fetchShorts = async (pageToken?: string): Promise<any> => {
    const params = localeSearchParams();
    if (pageToken) params.append('pageToken', pageToken);
    const url = `${API_URLS.SHORTS}?${params.toString()}`;
    return await getData(url);
}

const saveCollection = async (collection: any): Promise<any> => {
    /* const collection = {
         title: 'Business',
        description: 'Business World',
        tags: ['f3d1b665-b659-4b58-a515-f95ac1ecfecb', '4d90fe85-71ee-4a05-a705-72941bb80092']
    }
    */
    return await postData(API_URLS.SAVE_COLLECTION, collection);
}

const getThinkoCollections = async (): Promise<any[]> => {
    return (await getData(API_URLS.THINKO_COLLECTIONS)).slice(0,2);
}

const getCollectionsForUser = async (): Promise<any[]> => {
    return await getData(API_URLS.COLLECTIONS);
}

const deleteCollection = async (collectionId: string): Promise<any> => {
    const url = buildPath(API_URLS.DELETE_COLLECTIONS, { collectionId });
    return await deleteData(url);
}

const getRecommendations = async (limit?: number): Promise<RecommendedCollection[]> => {
    const params = new URLSearchParams({});
    if (limit) params.append('limit', limit.toString());
    const url = buildPath(API_URLS.GET_RECOMMENDATIONS, { query: params.toString() });
    return await getData(url);
}
const getCollectionById = async (collectionId: string): Promise<any> => {
    const url = buildPath(API_URLS.GET_COLLECTION_By_ID, { collectionId });
    return await getData(url);
}

const getTags = async (limit?: string, type?: string): Promise<any[]> => {
    const params = new URLSearchParams({});
    if (type) params.append('type', type);
    const url = buildPath(API_URLS.TAGS, { query: params.toString() });
    return await getData(url);
}

const getContextualTags = async (tagIds: string[], partialTagName: string, sortPreference?: string, limit?: string): Promise<any> => {
    return await postData(API_URLS.CONTEXTUAL_TAGS, { tagIds: tagIds, limit: limit || 20, partialTagName: partialTagName || '', sortPreference: sortPreference });
};

const getAiResponse = async (question: string): Promise<any[]> => {
    const params = localeSearchParams();
    const url = buildPath(API_URLS.AI_QUERY, { query: params.toString() });
    return await postData(url, { question });
}

const getArticleAiResponse = async (articleId: string, question: string,): Promise<any[]> => {
    const params = localeSearchParams();
    const url = buildPath(API_URLS.AI_ARTICLE_QUERY, { eventId: articleId, query: params.toString() });
    return await postData(url, { question });
}

const getFaqs = async (articleId: string): Promise<any[]> => {
    const params = localeSearchParams();
    const url = buildPath(API_URLS.AI_ARTICLE_FAQS, { eventId: articleId, query: params.toString() });
    return await getData(url);
}
const trigerTrendingJob = async (categoryId: string): Promise<any[]> => {
    const params = new URLSearchParams({ categoryId });
    const url = buildPath(API_URLS.AI_ARTICLE_FAQS, { query: params.toString() });
    return await getData(url);
}

/**
 * getTranscription
 * Supports:
 *   - Web: Blob-based upload
 *   - React Native (Expo): File URI upload
 */
const getTranscription = async (input: string | BlobPart[]) => {
    try {
        let formData = getFormData(input)
        // Always get fresh token
        const idToken = await getFirebaseToken();

        const response = await apiClient.post(API_URLS.TRANSCRIBE, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${idToken}`,
            },
            transformRequest: (data: any) => data, // prevent axios from altering FormData
            timeout: 30000,
        });
        return response.data?.text ?? '';

    } catch (error: any) {
        console.error('[Transcription] Upload failed:', error.message);
        throw error;
    }
};

const getFormData = (input: string | BlobPart[]) => {
    const formData = new FormData();

    /* ---------- 🌐 Web upload (Blob[]) ---------- */
    if (Platform.OS === 'web' && Array.isArray(input)) {
        const blob = new Blob(input, { type: 'audio/webm' });
        formData.append('file', blob, 'recording.webm');
    }

    /* ---------- 📱 React Native upload (URI) ---------- */
    else if (typeof input === 'string') {
        // input = local file URI, e.g. "file:///data/user/0/.../recording.m4a"
        formData.append('file', {
            uri: input,
            name: 'recording.webm',
            type: 'audio/webm',
        } as any);
    }

    /* ---------- 🧩 Handle invalid input ---------- */
    else {
        throw new Error('Invalid transcription input — expected URI or BlobPart[]');
    }
    return formData;

}

const localeSearchParams = () => {
    const params = new URLSearchParams({
        locale: i18n.language,
    });
    return params;
}

const getData = async (url: string) => {
    const response = await apiClient.get(url);
    return response?.data;
    // const debouncedGetData = debounce(getDataUsingDebounce, 300);
    // return debouncedGetData(url);
}
const getDataUsingDebounce = async (url: string) => {
    const response = await apiClient.get(url);
    return response?.data;
}
const postData = async (url: string, body: any) => {
    const response = await apiClient.post(url, body);
    return response?.data;
}

const deleteData = async (url: string) => {
    const response = await apiClient.delete(url);
    return response?.data;
}

const buildPath = (path: string, params: Record<string, string | number>) => {
    return path.replace(/{(\w+)}/g, (_, key) => String(params[key]));
}
export {
    getConfig,
    getUser,
    updateUser,
    registerDevice,
    migrateAnonymousUser,
    fetchCategories,
    fetchArticles,
    fetchSources,
    fetchFullArticleDetails,
    getTranscription,
    fetchReels,
    fetchShorts,
    saveCollection,
    getThinkoCollections,
    getCollectionsForUser,
    previewArticles,
    getCollectionById,
    deleteCollection,
    getRecommendations,
    getTags,
    getContextualTags,
    getAiResponse,
    getArticleAiResponse,
    getFaqs,
    APP_CONFIG_KEY
}
